import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import productService, { ProductFilterParams } from '../services/productService';
import ProductForm from '../components/products/ProductForm';

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 24,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  textAlign: 'left',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  background: '#ffffff',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(239,232,213,0.5)',
  verticalAlign: 'middle',
};

const ProductsPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState<ProductFilterParams>({
    page: 1,
    limit: 20,
    search: '',
    isActive: true,
  });

  // Fetch products
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productService.getProducts(filters),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['product-stats'],
    queryFn: () => productService.getProductStats(),
  });

  const products = data?.data || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: e.target.value, page: 1 });
  };

  const handleFilterChange = (key: keyof ProductFilterParams, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const getItemTypeBadgeStyle = (itemType: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 3,
      fontFamily: "'Inter', sans-serif",
      fontSize: 9,
      fontWeight: 400,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    };
    if (itemType === 'SERVICE') return { ...base, background: 'rgba(92,78,51,0.2)', color: '#d97706', border: '1px solid rgba(92,78,51,0.3)' };
    if (itemType === 'INVENTORY') return { ...base, background: 'rgba(90,180,130,0.1)', color: '#5ab482', border: '1px solid rgba(90,180,130,0.2)' };
    if (itemType === 'SERIALIZED') return { ...base, background: 'rgba(150,100,200,0.1)', color: '#a064c8', border: '1px solid rgba(150,100,200,0.2)' };
    return { ...base, background: 'rgba(239,232,213,0.6)', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.2)' };
  };

  const getStockBadge = (product: any) => {
    if (product.itemType === 'SERVICE') {
      return <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778' }}>N/A</span>;
    }
    if (product.isLowStock) {
      return <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#c0504a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Low Stock</span>;
    }
    const totalStock = product.totalStock || 0;
    const available = product.availableStock || 0;
    const reserved = product.totalReserved || 0;
    const rented = product.rentedCount || 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ab482', fontWeight: 500 }}>{totalStock} Total</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706' }}>{available} Available</span>
        {reserved > 0 && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c08040' }}>{reserved} Reserved</span>}
        {rented > 0 && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#a064c8' }}>{rented} Rented</span>}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
          Inventory
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
          Products &amp; Inventory
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
          Manage your products, services, and inventory items
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            { label: 'Total Products', value: stats.totalProducts, color: '#1c2033' },
            { label: 'Service Items', value: stats.serviceItems, color: '#d97706' },
            { label: 'Inventory Items', value: stats.inventoryItems, color: '#5ab482' },
            { label: 'Low Stock Items', value: stats.lowStockItems, color: '#c0504a' },
          ].map((stat) => (
            <div key={stat.label} style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '20px 24px' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 10 }}>{stat.label}</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters and Actions */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <input
              type="text"
              placeholder="Search by name, SKU, or description..."
              value={filters.search}
              onChange={handleSearch}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <select
            value={filters.itemType || ''}
            onChange={(e) => handleFilterChange('itemType', e.target.value || undefined)}
            style={selectStyle}
          >
            <option value="">All Types</option>
            <option value="SERVICE">Service</option>
            <option value="INVENTORY">Inventory</option>
            <option value="SERIALIZED">Serialized</option>
          </select>
          <select
            value={filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'}
            onChange={(e) =>
              handleFilterChange('isActive', e.target.value === 'all' ? undefined : e.target.value === 'active')
            }
            style={selectStyle}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
            <input
              type="checkbox"
              checked={filters.lowStock || false}
              onChange={(e) => handleFilterChange('lowStock', e.target.checked || undefined)}
              style={{ accentColor: '#d97706' }}
            />
            Low Stock Only
          </label>
          <button onClick={() => setShowCreateModal(true)} style={primaryButtonStyle}>
            + Create Product
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>Loading products...</div>
          </div>
        ) : products.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 16 }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>No products found</div>
            <button onClick={() => setShowCreateModal(true)} style={primaryButtonStyle}>
              Create Your First Product
            </button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>SKU / Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Category</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Price</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Stock</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product: any) => (
                    <tr
                      key={product.id}
                      style={{ transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Link
                            to={`/products/${product.id}`}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#d97706', textDecoration: 'none', fontWeight: 500 }}
                          >
                            {product.name}
                          </Link>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#8a8778', letterSpacing: '0.1em' }}>{product.sku}</span>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={getItemTypeBadgeStyle(product.itemType)}>{product.itemType}</span>
                      </td>
                      <td style={tdStyle}>{product.category?.name || '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#1c2033', fontWeight: 500 }}>
                        BD {product.unitPrice.toFixed(3)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#6b6a5c' }}>
                        BD {product.costPrice.toFixed(3)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {getStockBadge(product)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 3,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 9,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          background: product.isActive ? 'rgba(90,180,130,0.1)' : 'rgba(239,232,213,0.6)',
                          color: product.isActive ? '#5ab482' : '#8a8778',
                          border: product.isActive ? '1px solid rgba(90,180,130,0.2)' : '1px solid rgba(92,78,51,0.2)',
                        }}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <Link
                          to={`/products/${product.id}`}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#d97706', textDecoration: 'none', letterSpacing: '0.1em', marginRight: 16 }}
                        >
                          View
                        </Link>
                        <Link
                          to={`/products/${product.id}/edit`}
                          style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', textDecoration: 'none', letterSpacing: '0.1em' }}
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(92,78,51,0.15)' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>
                Showing{' '}
                <span style={{ color: '#5b5a52' }}>{((pagination.page - 1) * filters.limit!) + 1}</span>
                {' '}to{' '}
                <span style={{ color: '#5b5a52' }}>{Math.min(pagination.page * filters.limit!, pagination.total)}</span>
                {' '}of{' '}
                <span style={{ color: '#5b5a52' }}>{pagination.total}</span>
                {' '}results
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(92,78,51,0.25)',
                    borderRadius: 4,
                    padding: '7px 16px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    color: pagination.page === 1 ? '#c9c2ab' : '#5b5a52',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    opacity: pagination.page === 1 ? 0.5 : 1,
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(92,78,51,0.25)',
                    borderRadius: 4,
                    padding: '7px 16px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    color: pagination.page >= pagination.totalPages ? '#c9c2ab' : '#5b5a52',
                    cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer',
                    textTransform: 'uppercase',
                    opacity: pagination.page >= pagination.totalPages ? 0.5 : 1,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Product Modal */}
      {showCreateModal && (
        <ProductForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default ProductsPage;
